import React, { useRef } from 'react';
import styled from 'styled-components';
import Branded from '../components/Branded';

const Section = styled.section`
  background: #fff;
  padding: 100px 0;

  @media (max-width: 768px) {
    padding: 64px 0;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Header = styled.div`
  margin-bottom: 48px;
  text-align: center;

  .tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f08020;
    margin-bottom: 12px;
  }

  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #111;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
`;

const ScrollWrap = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  cursor: grab;
  user-select: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Track = styled.div`
  display: flex;
  gap: 20px;
  min-width: min-content;
  padding-bottom: 4px;
`;

const Card = styled.div`
  flex: 0 0 300px;
  position: relative;
  cursor: default;

  @media (max-width: 640px) {
    flex: 0 0 220px;
  }

  .card-img {
    position: relative;
    width: 100%;
    height: 320px;
    overflow: hidden;
    border-radius: 8px;

    @media (max-width: 640px) {
      height: 270px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.5) 0%,
        transparent 60%
      );
    }
  }

  &:hover .card-img img {
    transform: scale(1.04);
  }

  .card-num {
    position: absolute;
    top: 14px;
    left: 14px;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    z-index: 3;
  }

  .card-text {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px;
    z-index: 2;
  }

  .card-title {
    font-size: 15px;
    font-weight: 500;
    color: #fff;
    line-height: 1.3;
    margin-bottom: 6px;
    -webkit-text-stroke: 1px #000;
    paint-order: stroke fill;
  }

  .card-desc {
    font-size: 12px;
    color: #fff;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-text-stroke: 0.5px #000;
    paint-order: stroke fill;

    /* Brand blue is too dark against the photo overlay */
    .brand-space {
      color: #8fc4f7;
    }
  }

  /* Pure dark layer behind the text — only this animates on hover.
     The text (.card-text) stays fixed on top. */
  .card-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 32%;
    background: rgba(0, 0, 0, 0.8);
    transform: scaleY(0);
    transform-origin: bottom;
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;
    z-index: 1;
  }

  &:hover .card-overlay {
    transform: scaleY(1);
  }

  /* Outline only in the resting state; on hover the solid dark panel
     provides the contrast, so the stroke is dropped. */
  &:hover .card-title,
  &:hover .card-desc {
    color: #fff;
    -webkit-text-stroke: 0;
  }
`;

const ScrollBar = styled.div`
  margin-top: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .track {
    width: 200px;
    height: 2px;
    background: rgba(19, 120, 229, 0.1);
    border-radius: 1px;
    position: relative;
    overflow: hidden;
  }

  .thumb {
    height: 100%;
    background: #f08020;
    border-radius: 1px;
    transition: width 0.15s ease;
  }
`;

export default function Philosophy({ tag, title, items }) {
  const data = items || [];
  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const [scrollPct, setScrollPct] = React.useState(0);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollPct(max > 0 ? (el.scrollLeft / max) * 100 : 0);
  };

  const onMouseDown = e => {
    isDragging.current = true;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = 'grabbing';
  };

  const onMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  const onMouseMove = e => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  React.useEffect(() => {
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, []);

  if (!data.length) return null;

  const thumbWidth = Math.max(20, (4 / data.length) * 100);

  return (
    <Section>
      <Container>
        <Header>
          {tag && <div className="tag">{tag}</div>}
          <h2>{title || 'Design Philosophy'}</h2>
        </Header>
      </Container>

      <Container>
        <ScrollWrap
          ref={scrollRef}
          onScroll={onScroll}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseEnter={() => document.body.classList.add('cursor-grab')}
          onMouseLeave={() => document.body.classList.remove('cursor-grab')}
        >
          <Track>
            {data.map(({ _key, title: t, description, image }, idx) => (
              <Card key={_key}>
                <div className="card-img">
                  {image?.asset?.url ? (
                    <img
                      src={image.asset.url + '?w=400&auto=format'}
                      alt={t}
                      loading="lazy"
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#0B5DB8',
                      }}
                    />
                  )}
                  <span className="card-num">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="card-overlay" />
                  <div className="card-text">
                    <div className="card-title">{t}</div>
                    <Branded as="p" className="card-desc" text={description} />
                  </div>
                </div>
              </Card>
            ))}
          </Track>
        </ScrollWrap>
      </Container>

      <Container>
        <ScrollBar>
          <div className="track">
            <div
              className="thumb"
              style={{
                width: `${thumbWidth}%`,
                marginLeft: `${(scrollPct * (100 - thumbWidth)) / 100}%`,
              }}
            />
          </div>
        </ScrollBar>
      </Container>
    </Section>
  );
}
